import Head from "next/head";
import CLMHero2 from "components/CLM/CLMHero2";
import CTASection from "components/CLM/CTASection";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import FAQ2 from "components/CLM/FAQ2";
import { CLMButtonPrimary } from "components/CLM/v3/Buttons";
import { BASE_ASSETS_URL } from "@constants/constants";

const CLMHeroData = {
  // title: "Reporting",
  heading: "Legally-binding eSignatures within BoloSign CLM",
  description:
    "Secure digital signatures, legally binding and built for contracts—only with BoloSign.",
  image: `${BASE_ASSETS_URL}/v2/clm/clm/hero-esignature.png`,
  btnText: "Request a Demo",
  btnUrl: "/clm/request-demo",
};

const eSignatureFaqData = [
  {
    question: "What is BoloSign eSignature?",
    answer:
      "BoloSign eSignature is a secure digital signing solution built into the BoloSign platform, allowing users to obtain legally binding electronic signatures without relying on paper or third-party tools. It supports industry-standard cryptographic methods to ensure every signature is authentic and verifiable. With BoloSign eSignature, you can streamline your document approvals in a single, centralized interface.",
  },
  {
    question: "How does BoloSign simplify the document signing workflow?",
    answer:
      "By replacing manual printing and mailing with an end-to-end digital process, BoloSign lets you upload documents, assign signers, and track progress in real time—all from your dashboard. Signatories receive an email link to review and sign on any device, and you can monitor exactly when each party has signed. This unified, browser-based experience eliminates bottlenecks and keeps everyone on the same page.",
  },
  {
    question: "How does BoloSign protect my documents and signatures?",
    answer:
      "Every transaction in BoloSign is secured using industry-grade encryption, ensuring documents remain confidential both in transit and at rest. Detailed audit logs record each action—who opened, viewed, or signed a file and when—so you have a transparent trail for compliance or disputes. You can also add optional one-time passwords (OTPs) to signature links for an extra layer of identity verification.",
  },
  {
    question: "Is BoloSign eSignature legally recognized worldwide?",
    answer:
      "Yes. BoloSign eSignature meets major global electronic signature regulations—such as ESIGN (U.S.), eIDAS (EU), and additional standards like UETA, ensuring signatures obtained through our system are enforceable in most jurisdictions. By adhering to these frameworks, BoloSign guarantees that your electronically signed agreements carry the same legal weight as traditional wet-ink signatures.",
  },
  {
    question: "Can I send signature requests from any device using BoloSign?",
    answer:
      "Absolutely. Whether your signers are on a desktop, laptop, tablet, or smartphone, they can open the link, review the document, and apply their electronic signature without installing any software. This device-agnostic approach ensures no one is blocked by operating system or hardware limitations—signing is as simple as clicking a link.",
  },
  {
    question:
      "Does BoloSign send notifications and reminders about pending signatures?",
    answer:
      "Yes. As soon as you send a signature request, BoloSign delivers an email notification to each signer with a direct link to the document. If a signer hasn’t acted within your specified timeframe, automated reminders are dispatched to keep the process moving. You’ll also receive real-time status updates whenever someone completes or declines a signature.",
  },
  {
    question: "Can I set an expiration date for a signature link in BoloSign?",
    answer:
      "You can. When preparing a document for signature, simply choose an expiration date for each signing link to ensure that agreements are executed within a defined window. Once the link expires, signers can no longer access the document, helping you maintain control over timing and enforce deadlines.",
  },
  {
    question: "Can I customize the signing order for multiple signers?",
    answer:
      "Yes. BoloSign allows you to define a sequential signing order when multiple parties are involved. You can specify exactly who signs first, second, and so on—ensuring critical stakeholders review and approve documents in the right sequence. Alternatively, you can enable parallel signing if order doesn't matter, allowing all signers to act simultaneously. This flexibility keeps your workflow aligned with your business needs.",
  },
];

const Esignatures = () => {
  return (
    <>
      <Head>
        <title>eSignatures - BoloSign CLM</title>
        <meta
          name="description"
          content="Secure, legally binding eSignatures built for contracts—only with BoloSign CLM."
        />
        <meta
          name="keywords"
          content="BoloSign eSignature, digital signatures, electronic signatures, contract signing, secure eSignatures, legally binding signatures, contract lifecycle management, CLM eSignatures"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/esignatures/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />
      <div className="text-center bg-cream pt-20 md:pt-32 ">
        <h2 className="text-4xl md:text-5xl xl:text-6xl px-6 md:px-0 md:-mb-10 text-start md:text-center">
          Close contracts faster with BoloSign CLM
        </h2>

        <EqualDivisionSection
          heading="Faster contract execution with digital signatures"
          description="BoloSign’s native eSign lets you collect legally binding signatures instantly and securely."
          checks={[
            "Skip paper or extra tools; sign anywhere, on any device.",
            "Monitor progress live and auto-save contracts in your BoloSign repository.",
          ]}
          image={`${BASE_ASSETS_URL}/v2/clm/clm/signature-coat.png`}
          bgColor="bg-cream"
          imageRight={true}
        />
        <EqualDivisionSection
          // title=""
          heading="Secure and globally compliant eSignatures"
          description="Protect every signature with enterprise-grade security and international compliance."
          checks={[
            "Control access – Set expiry dates on contract links.",
            "Verify identity – Use OTPs and unique signer IDs.",
            "Track everything – Get detailed logs of every action.",
          ]}
          image={`${BASE_ASSETS_URL}/v2/clm/clm/signature-financial-review.png`}
          bgColor="bg-white"
          imageRight={false}
        />

        <EqualDivisionSection
          // title=""
          heading="Contract management and eSigning in one platform"
          description="Manage the full contract lifecycle—from draft to signature—right inside BoloSign."
          checks={[
            "Full context at signing – View revision history and key details.",
            "Custom workflows – Set up signing and approvals your way.",
            "Track at scale – Monitor eSign status with smart reports.",
          ]}
          image={`${BASE_ASSETS_URL}/v2/clm/clm/signature-add-signature.png`}
          bgColor="bg-cream"
          imageRight={true}
        />

        <EqualDivisionSection
          // title=""
          heading="Collect signatures effortlessly, anywhere"
          description="Speed up signing with flexible options and real-time notifications."
          checks={[
            "Sign on any device – Laptop, phone, or tablet.",
            "Stay updated – Get alerts via BoloSign Dashboard or email.",
            "Flexible formats – Sign via link or directly on DOC, DOCX, or PDF.",
          ]}
          image={`${BASE_ASSETS_URL}/v2/clm/clm/signature-new-contract.png`}
          bgColor="bg-white"
          imageRight={false}
        />
      </div>
      <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={eSignatureFaqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default Esignatures;
